import { Stack } from '@mui/material';
import { SetOptional } from 'type-fest';

import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import {
  FormModal,
  FormModalProps,
} from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions/FormModalActions';
import { FormProvider, useFormContext } from '@/components/react-hook-form';
import { useForm, useSubmitSuccessHandler } from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';

interface RegenerateMemoModalFormShape {
  specificInstructions: string;
}

const DEFAULT_VALUES: RegenerateMemoModalFormShape = {
  specificInstructions: '',
};

type FormModalOptionalProps = SetOptional<FormModalProps, 'heading'>;

interface RegenerateMemoModalProps extends FormModalOptionalProps {
  onSubmit: (specificInstructions?: string) => Promise<void>;
  loading: boolean;
}

function RegenerateMemoModalInner({
  onClose,
  onSubmit,
  loading,
  ...formModalProps
}: RegenerateMemoModalProps) {
  const { formRef, handleSave } = useFormSaveHandler();
  const { control, formState, handleSubmit, reset, shouldBlockNavigation } =
    useFormContext<RegenerateMemoModalFormShape>();

  const handleClose = () => {
    reset(DEFAULT_VALUES);
    onClose();
  };

  const handleFormSubmit = handleSubmit((formData) => {
    return onSubmit(formData.specificInstructions.trim() || undefined);
  });

  useSubmitSuccessHandler(() => {
    handleClose();
  });

  return (
    <FormModal
      {...formModalProps}
      onClose={handleClose}
      heading="Regenerate memo"
      shouldBlockClose={shouldBlockNavigation}
      actions={
        <FormModalActions.Provider formState={formState}>
          <FormModalActions.CancelButton onClick={handleClose} />
          <FormModalActions.SaveButton
            onClick={handleSave}
            loading={loading}
            disabled={loading}
          >
            Regenerate memo
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <form ref={formRef} onSubmit={handleFormSubmit} noValidate>
        <Stack spacing={2}>
          <FormAwareTextInput<RegenerateMemoModalFormShape>
            control={control}
            fieldName="specificInstructions"
            label="Additional changes"
            placeholder="Optional, describe the improvements or areas of focus for this memo"
            multiline
            rows={4}
          />
        </Stack>
      </form>
    </FormModal>
  );
}

export function RegenerateMemoModal(props: RegenerateMemoModalProps) {
  const formMethods = useForm<RegenerateMemoModalFormShape>({
    defaultValues: DEFAULT_VALUES,
  });

  return (
    <FormProvider {...formMethods}>
      <RegenerateMemoModalInner {...props} />
    </FormProvider>
  );
}
